<script setup lang="ts">
import { computed, ref } from 'vue';
import { trackingOnboardingEvent } from '@/api/tracking';
import {
  AGENT_NAME_INFORMATION_RETRIEVAL,
  AGENT_TOP_ROUTE_NAME_INFORMATION_RETRIEVAL,
  PRODUCT_LOGO_ASSET,
  PRODUCT_LOGO_STYLE,
} from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import AppShellHeader from '@/components/layouts/app-shell/app-shell-header.vue';
import AppShell from '@/components/layouts/app-shell/app-shell.vue';
import HelpDropdown from '@/components/menu/help-dropdown.vue';
import LeftSectionMenu from '@/components/menu/left-section-menu-next.vue';
import MenuPanel from '@/components/menu/menu-panel.vue';
import NavigationDropdown from '@/components/menu/navigation-dropdown.vue';
import SettingsDropdown from '@/components/menu/settings-dropdown.vue';
import UserMenuDropdown from '@/components/menu/user-menu-dropdown.vue';
import ArticleShare from '@/components/modals/article-share.vue';
import ConfirmationModal from '@/components/modals/confirmation-modal.vue';
import TranslateNotificationModal from '@/components/modals/translate-notification-modal.vue';
import NotificationPopupDialog from '@/components/notifications/header/popup-dialog.vue';
import KeywordSearchBar from '@/components/search/keyword-search-bar-next.vue';
import { useAppSetup } from '@/utils/composables/useAppSetup';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { switchLayout } from '@/utils/composables/useLayoutPreference';
import { useLeftMenuLayout } from '@/utils/composables/useLeftMenuLayout';
import { useTranslateNotification } from '@/utils/composables/useTranslateNotification';
import { useStore } from '@/utils/vue';

const store = useStore();

useAppSetup();

const confirmationModalOpen = computed(
  () => store.state.confirmationModal.confirmationModalOpen,
);
const { isOpen: translateNotificationOpen, close: closeTranslateNotification } =
  useTranslateNotification();

const isArticleShareOpen = ref(false);

const { panelWidth, hasPanel, isDragging, toggle, startDrag, activeMenu } =
  useLeftMenuLayout();

const { getCurrentPathForTracking } = useCurrentPathForTracking();
const trackingClickRestart = async () => {
  await trackingOnboardingEvent('onboarding_restart_clicked', {
    page: { url: getCurrentPathForTracking() },
    feature: 'onboarding_restart',
  });
};

const backToLegacyLayout = () => switchLayout(true);
</script>

<template>
  <AppShell
    :panel-width="panelWidth"
    :has-panel="hasPanel"
    :is-resizing="isDragging"
    @toggle="toggle"
    @drag-start="startDrag"
  >
    <template #header>
      <AppShellHeader>
        <template #brand>
          <NavigationDropdown />
          <router-link
            class="brand-link"
            :to="{ name: AGENT_TOP_ROUTE_NAME_INFORMATION_RETRIEVAL }"
          >
            <img
              class="brand-logo"
              :src="PRODUCT_LOGO_ASSET"
              :style="PRODUCT_LOGO_STYLE"
            />
            <div class="c-title-bold--s brand-subtitle">
              {{ AGENT_NAME_INFORMATION_RETRIEVAL }}
            </div>
          </router-link>
        </template>
        <template #center>
          <KeywordSearchBar class="header-search-bar" />
        </template>
        <template #actions>
          <button
            class="c-text-regular--xs header-layout-switch"
            v-tooltip.bottom="'以前のデザインにいつでも戻すことができます。'"
            @click="backToLegacyLayout"
          >
            <DgrIcon name="arrow-undo" size="xs" :keep-fill="false" />
            旧デザインに戻す
          </button>
          <button
            class="header-icon-button"
            aria-label="外部リンクを共有"
            v-tooltip.bottom="'外部リンクを共有'"
            @click="isArticleShareOpen = true"
          >
            <DgrIcon name="clip-plus" class="header-icon" />
          </button>
          <HelpDropdown>
            <template #dropdown-items>
              <hr class="dropdown-divider" />
              <RouterLink
                :to="{ name: 'onboardingWelcome' }"
                @click="trackingClickRestart"
                class="dropdown-item"
              >
                セットアップをやり直す
              </RouterLink>
            </template>
          </HelpDropdown>
          <SettingsDropdown
            :agent-settings-label="`${AGENT_NAME_INFORMATION_RETRIEVAL}の設定`"
            agent-settings-description="このエージェントのメール配信や通知などの設定を管理します。"
            :agent-settings-route="{ name: 'userSettingsEmail' }"
          />
          <UserMenuDropdown />
        </template>
      </AppShellHeader>
    </template>

    <template #rail>
      <LeftSectionMenu :active-menu="activeMenu" />
    </template>

    <template #panel>
      <MenuPanel />
    </template>

    <template #content>
      <router-view></router-view>
    </template>
  </AppShell>

  <ConfirmationModal v-if="confirmationModalOpen"></ConfirmationModal>
  <TranslateNotificationModal
    v-if="translateNotificationOpen"
    @close="closeTranslateNotification"
  ></TranslateNotificationModal>
  <ArticleShare
    v-if="isArticleShareOpen"
    v-model:open="isArticleShareOpen"
  ></ArticleShare>
  <NotificationPopupDialog></NotificationPopupDialog>
  <div id="modal-teleport-target"></div>
</template>

<style lang="scss" scoped>
.brand-link {
  display: flex;
  align-items: end;
  height: 32px;
  padding-bottom: 7px;
  box-sizing: border-box;
}

.brand-subtitle {
  display: flex;
  align-items: end;
  height: 32px;
  line-height: 1;
  margin-left: 8px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.header-search-bar {
  margin-left: 80px;
}

.header-layout-switch {
  display: flex;
  gap: 6px;
  height: unset;
  padding: 8px;
  color: $color-gray800;
  border: none;
  background: #ffffff;

  &:hover {
    text-decoration: underline;
    background: #ffffff;
  }
}

.header-icon-button {
  position: relative;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  padding: 0;
  border: none;
  border-radius: 4px;
  background-color: $color-white;

  &:hover {
    background-color: $color-gray200;
  }

  .header-icon {
    width: 21px;
    height: 21px;
    margin: 5.5px;
  }
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 4px 12px;
  text-align: left;
  border: none;
  background: none;
  cursor: pointer;

  &:hover {
    background: $color-gray200;
  }
}

.dropdown-divider {
  border-top: 1px solid $color-gray400;
  border-bottom: 0;
}
</style>
